/** @jsx jsx */
import { defineMessages, FormattedMessage } from 'react-intl-next';
import { css, jsx } from '@compiled/react';
import { useCallback } from 'react';

import { Flex, Box, Text, xcss } from '@atlaskit/primitives';
import Image from '@atlaskit/image';
import Heading from '@atlaskit/heading';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import SuccessIcon from '@atlaskit/icon/core/success';

import { HTV2DismissButton } from './HTV2DismissButton';
import { type OnboardingAction } from './useOnboardingActions';

const contentContainerStyles = xcss({
	paddingBlock: 'space.200',
	paddingInline: 'space.200',
	height: '100%',
});

const cardStyles = xcss({
	overflow: 'hidden',
	border: `1px solid ${token('color.border')}`,
	borderRadius: '8px',
	background: token('elevation.surface'),
});

const completedCardStyle = xcss({
	alignSelf: 'start',
});

const imageContainerStyles = xcss({
	position: 'relative',
});

const imageStyles = css({ display: 'block', width: '100%' });

const descriptionContainerStyles = xcss({
	// Ensure the description text is always 3 lines tall
	height: '4.28em', // (20px line height * 3 lines) / 14px font size
});

const i18n = defineMessages({
	dismissButtonLabel: {
		id: 'experiment-home-onboarding-takeover-v2.dismiss-card-button-label',
		defaultMessage: 'Dismiss this action',
		description:
			'This text is shown as a label for a dismiss/cross icon for accessibility. The icon can then be used to dismiss the card. The placeholder {title} is replaced with the title of the card.',
	},
});

export const HTV2ActionCard = ({
	action,
	dismiss,
	complete,
	onboardingPeriodDay,
}: {
	action: OnboardingAction;
	dismiss?: (id: OnboardingAction['id']) => void;
	complete?: (id: OnboardingAction['id']) => void;
	onboardingPeriodDay?: number;
}) => {
	const Cta = action.cta;
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const handleClick = useCallback(() => {
		complete?.(action.id);
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'homeOnboardingTakeoverV2ActionCta',
				attributes: {
					onboardingAction: action.id,
					onboardingPeriodDay,
				},
			},
		}).fire();
	}, [complete, createAnalyticsEvent, action.id, onboardingPeriodDay]);

	const handleDismiss = useCallback(() => {
		if (dismiss) {
			dismiss(action.id);
		}
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'homeOnboardingTakeoverV2ActionDismiss',
				attributes: {
					onboardingAction: action.id,
					onboardingPeriodDay,
				},
			},
		}).fire();
	}, [dismiss, createAnalyticsEvent, action.id, onboardingPeriodDay]);

	const isCompleted = action.state === 'completed';
	return (
		<Flex
			testId={isCompleted ? 'htv2-onboarding-action-completed' : 'htv2-onboarding-action-pending'}
			direction="column"
			xcss={[cardStyles, isCompleted && completedCardStyle]}
		>
			<Box xcss={imageContainerStyles}>
				<Image css={imageStyles} src={action.image.light} srcDark={action.image.dark} alt="" />
				<HTV2DismissButton label={{ ...i18n.dismissButtonLabel }} onDismiss={handleDismiss} />
			</Box>
			<Flex
				xcss={contentContainerStyles}
				justifyContent="space-between"
				direction="column"
				gap="space.100"
			>
				<Flex direction="column" gap="space.100">
					<Flex direction="row" gap="space.100">
						<Heading size="xsmall">
							<FormattedMessage {...action.title} />
						</Heading>
						{isCompleted && <SuccessIcon color="var(--ds-icon-success)" label="completed" />}
					</Flex>
					{!isCompleted && (
						<Box xcss={descriptionContainerStyles}>
							<Text maxLines={3}>
								<FormattedMessage {...action.description} />
							</Text>
						</Box>
					)}
				</Flex>
				{!isCompleted && (
					<Flex justifyContent="end">
						<Cta onClick={handleClick} />
					</Flex>
				)}
			</Flex>
		</Flex>
	);
};
